import React, { ChangeEvent, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { fsDb, storage } from '../../../core/firebase/config';
import { Box, Container } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

// export const ImageUploader: React.FC = () => {
//     const [file, setFile] = useState<File | null>(null);
//     const [progress, setProgress] = useState<number>(0);
//     const [loading, setLoading] = useState<boolean>(false);

//     const handleUpload = async () => {
//         if (!file) return;

//         setLoading(true);
//         const storageRef = ref(storage, `gallery/${file.name}`);
//         const uploadTask = uploadBytesResumable(storageRef, file);

//         uploadTask.on(
//             'state_changed',
//             (snapshot) => {
//                 const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100; 
//                 setProgress(pct);
//             },
//             (error) => {
//                 console.error("Upload Error:", error);
//                 setLoading(false);
//             },
//             async () => {
//                 // Handle successful upload
//                 const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
//                 const minHeight = 300;
//                 const maxHeight = 600;
//                 const data = {
//                     id: uuidv4(),
//                     img: downloadURL,
//                     storagePath: storageRef.fullPath,
//                     fileName: uuidv4(),
//                     height: Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight,
//                     createdAt: serverTimestamp(),
//                 }
//                 console.error("🚀 ~ handleUpload ~ data:", data)

//                 // Save to Firestore
//                 await addDoc(collection(fsDb, 'dev/tables/gallery'), data);

//                 setLoading(false);
//                 setFile(null);
//                 setProgress(0);
//             }
//         );
//     };

//     return (
//        <>
//         <Container>
//                 <Box p={16}>
//                     <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
//                     <button onClick={handleUpload} disabled={!file || loading}>
//                         {loading ? `Uploading... ${Math.round(progress)}%` : 'Upload'}
//                     </button>
//                 </Box>
//         </Container>
//        </>
//     );
// };


interface UploadStatus {
    total: number;
    current: number;
    status: 'idle' | 'uploading' | 'success' | 'error';
}

export const ImageUploader: React.FC = () => {
    const [uploadInfo, setUploadInfo] = useState<UploadStatus>({
        total: 0,
        current: 0,
        status: 'idle'
    });

    const uploadFile = async (file: File) => {
        // 1. Storage Path: Preserve local folder structure
        // Path looks like: "root_folder/sub_folder/image.jpg"
        const storagePath = `gallery/${file.name}`;
        const storageRef = ref(storage, storagePath);

        // 2. Upload to Storage
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        const minHeight = 350;
        const maxHeight = 600;
        const data = {
            id: uuidv4(),
            img: downloadURL,
            storagePath: storageRef.fullPath,
            fileName: uuidv4(),
            height: Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight,
            createdAt: serverTimestamp(),
        }
        console.error("🚀 ~ handleUpload ~ data:", data)

        // 3. Create Firestore Entry
        await addDoc(collection(fsDb, "dev/tables/gallery"), data);
    };

    const handleFolderSelect = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Filter for images only (ignore .DS_Store, etc.)
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));

        setUploadInfo({ total: imageFiles.length, current: 0, status: 'uploading' });

        // Processing in chunks of 3 to avoid network congestion
        const CONCURRENCY_LIMIT = 3;
        const results = [];

        for (let i = 0; i < imageFiles.length; i += CONCURRENCY_LIMIT) {
            const chunk = imageFiles.slice(i, i + CONCURRENCY_LIMIT);

            const chunkPromises = chunk.map(async (file) => {
                try {
                    await uploadFile(file);
                    setUploadInfo(prev => ({ ...prev, current: prev.current + 1 }));
                } catch (err) {
                    console.error(`Failed: ${file.name}`, err);
                }
            });

            await Promise.all(chunkPromises);
        }

        setUploadInfo(prev => ({ ...prev, status: 'success' }));
    };

    return (
        <div style={{ padding: '2rem', border: '1px solid #ccc', borderRadius: '8px' }}>
            <h3>Bulk Folder Uploader</h3>

            <input
                type="file"
                // @ts-ignore - Required for folder selection
                webkitdirectory=""
                directory=""
                multiple
                onChange={handleFolderSelect}
                disabled={uploadInfo.status === 'uploading'}
            />

            {uploadInfo.status === 'uploading' && (
                <div style={{ marginTop: '1rem' }}>
                    <progress value={uploadInfo.current} max={uploadInfo.total} style={{ width: '100%' }} />
                    <p>Uploading {uploadInfo.current} of {uploadInfo.total} images...</p>
                </div>
            )}

            {uploadInfo.status === 'success' && (
                <p style={{ color: 'green' }}>✅ Successfully uploaded {uploadInfo.total} images!</p>
            )}
        </div>
    );
};